// Phase A — Platform admin · Customer Relationships
//
// Manage tenant_type for existing tenants AND create new customer/carrier
// tenants linked to a forwarder. The user requested an inline tenant_type
// dropdown per row WITH a safety guard before flipping a tenant that
// already has deals/relationships — `useSetTenantType` calls /set-type
// without force first, captures a 409 + counts, then re-calls with
// ?force=true after the user confirms.
import React, { useMemo, useState } from "react";
import { GitBranch, Plus, Trash2, AlertTriangle, Building2, Users, Truck, Loader2, RefreshCw } from "lucide-react";
import {
  useCustomerRelationships,
  useCreateRelationship,
  useDeleteRelationship,
  useSetTenantType,
  useAdminAllTenants,
} from "../api/customerRelationships";

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const TENANT_TYPE_META = {
  forwarder: { Icon: Building2, color: "#79b5f0", label: "Forwarder" },
  customer:  { Icon: Users,     color: "#c89adb", label: "Customer" },
  carrier:   { Icon: Truck,     color: "#f3b14a", label: "Carrier" },
};

const TenantTypeBadge = ({ type }) => {
  const m = TENANT_TYPE_META[type] || TENANT_TYPE_META.forwarder;
  const { Icon } = m;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded px-2 py-0.5"
      style={{
        background: `${m.color}22`,
        color: m.color,
        border: `1px solid ${m.color}55`,
        ...mono(10, undefined, 600, "0.6px"),
        textTransform: "uppercase",
      }}
      data-testid={`tenant-type-badge-${type}`}
    >
      <Icon size={11} />
      {m.label}
    </span>
  );
};

const CreateModal = ({ open, onClose }) => {
  const create = useCreateRelationship();
  const { data: tenants } = useAdminAllTenants();
  const [form, setForm] = useState({
    tenant_name: "",
    relation_type: "customer",
    forwarder_tenant_id: "",
    primary_contact_name: "",
    primary_contact_email: "",
    primary_contact_initial_password: "",
  });
  const [error, setError] = useState(null);

  const forwarders = useMemo(
    () => (tenants || []).filter((t) => (t.tenant_type || "forwarder") === "forwarder"),
    [tenants]
  );

  if (!open) return null;

  const submit = async () => {
    setError(null);
    if (!form.tenant_name || form.tenant_name.length < 2) {
      setError("Tenant name must be at least 2 characters");
      return;
    }
    if (form.primary_contact_email && !form.primary_contact_initial_password) {
      setError("Initial password is required when a primary contact email is set (min 8 chars)");
      return;
    }
    if (form.primary_contact_initial_password && form.primary_contact_initial_password.length < 8) {
      setError("Initial password must be at least 8 characters");
      return;
    }
    try {
      const body = { ...form };
      Object.keys(body).forEach((k) => { if (body[k] === "") delete body[k]; });
      await create.mutateAsync(body);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to create");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
      data-testid="create-relationship-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-md w-full max-w-lg"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
      >
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "var(--text, #f0f2f8)" }}>
            New customer / carrier link
          </div>
          <button onClick={onClose} style={mono(11, "var(--text-dim, #9aa1b3)")} data-testid="create-relationship-cancel">Close</button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Relation type">
            <select
              value={form.relation_type}
              onChange={(e) => setForm({ ...form, relation_type: e.target.value })}
              className="w-full rounded px-2 py-2"
              style={inputStyle}
              data-testid="create-relationship-relation-type"
            >
              <option value="customer">Customer</option>
              <option value="carrier">Carrier</option>
            </select>
          </Field>
          <Field label="Tenant name">
            <input
              type="text"
              value={form.tenant_name}
              onChange={(e) => setForm({ ...form, tenant_name: e.target.value })}
              placeholder="e.g. Volkswagen Wolfsburg"
              className="w-full rounded px-2 py-2"
              style={inputStyle}
              data-testid="create-relationship-tenant-name"
            />
          </Field>
          <Field label="Linked forwarder (optional · defaults to first)">
            <select
              value={form.forwarder_tenant_id}
              onChange={(e) => setForm({ ...form, forwarder_tenant_id: e.target.value })}
              className="w-full rounded px-2 py-2"
              style={inputStyle}
              data-testid="create-relationship-forwarder"
            >
              <option value="">— Auto-select first forwarder —</option>
              {forwarders.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </Field>
          <div style={{ ...mono(9, "var(--text-dimmer, #6b7388)"), letterSpacing: "1px", textTransform: "uppercase", marginTop: 8 }}>
            Optional · Seed primary contact user
          </div>
          <Field label="Contact name">
            <input
              type="text"
              value={form.primary_contact_name}
              onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })}
              className="w-full rounded px-2 py-2"
              style={inputStyle}
              data-testid="create-relationship-contact-name"
            />
          </Field>
          <Field label="Contact email">
            <input
              type="email"
              value={form.primary_contact_email}
              onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })}
              className="w-full rounded px-2 py-2"
              style={inputStyle}
              data-testid="create-relationship-contact-email"
            />
          </Field>
          <Field label="Initial password (min 8 · admin shares manually)">
            <input
              type="text"
              value={form.primary_contact_initial_password}
              onChange={(e) => setForm({ ...form, primary_contact_initial_password: e.target.value })}
              className="w-full rounded px-2 py-2"
              style={inputStyle}
              data-testid="create-relationship-contact-password"
            />
          </Field>
          {error && (
            <div
              style={{
                background: "rgba(226,75,74,0.15)",
                border: "1px solid rgba(226,75,74,0.45)",
                color: "#ff8a8a",
                padding: "6px 10px",
                borderRadius: 4,
                ...mono(11),
              }}
              data-testid="create-relationship-error"
            >
              {String(error)}
            </div>
          )}
        </div>
        <div className="px-4 py-3 flex justify-end gap-2" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <button onClick={onClose} className="px-3 py-2 rounded" style={{ ...mono(11, "var(--text-dim, #9aa1b3)"), border: "1px solid rgba(255,255,255,0.15)" }}>Cancel</button>
          <button
            onClick={submit}
            disabled={create.isPending}
            className="px-3 py-2 rounded"
            style={{ background: "#185FA5", color: "#fff", ...mono(11, "#fff", 600), opacity: create.isPending ? 0.6 : 1 }}
            data-testid="create-relationship-submit"
          >
            {create.isPending ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

const inputStyle = {
  background: "var(--bg, #0a0c14)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "var(--text, #f0f2f8)",
  fontFamily: "'DM Mono', monospace",
  fontSize: 12,
};

const Field = ({ label, children }) => (
  <label className="block">
    <div style={{ ...mono(9, "var(--text-dim, #9aa1b3)"), letterSpacing: "1px", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
    {children}
  </label>
);

const TenantTypeFlip = ({ tenant }) => {
  const setType = useSetTenantType();
  const [pending, setPending] = useState(null); // {to, impact}

  const onChange = async (newType) => {
    if (newType === tenant.tenant_type) return;
    try {
      await setType.mutateAsync({ tenantId: tenant.id, tenantType: newType, force: false });
    } catch (e) {
      // 409 has data counts — surface confirmation
      const data = e?.response?.data?.detail;
      if (e?.response?.status === 409 && data && typeof data === "object") {
        setPending({ to: newType, impact: data });
      } else {
        alert(e?.response?.data?.detail || e?.message || "Failed");
      }
    }
  };

  const confirmForce = async () => {
    if (!pending) return;
    try {
      await setType.mutateAsync({ tenantId: tenant.id, tenantType: pending.to, force: true });
      setPending(null);
    } catch (e) {
      alert(e?.message || "Failed");
    }
  };

  return (
    <>
      <select
        value={tenant.tenant_type || "forwarder"}
        onChange={(e) => onChange(e.target.value)}
        className="rounded px-2 py-1"
        style={inputStyle}
        data-testid={`tenant-type-flip-${tenant.id}`}
        disabled={setType.isPending}
      >
        <option value="forwarder">Forwarder</option>
        <option value="customer">Customer</option>
        <option value="carrier">Carrier</option>
      </select>
      {pending && (
        <div
          className="fixed inset-0 z-[600] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.55)" }}
          onClick={() => setPending(null)}
          data-testid="tenant-type-flip-confirm-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-md w-full max-w-md"
            style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(243,177,74,0.55)" }}
          >
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={18} color="#f3b14a" />
                <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 15, color: "#f3b14a" }}>
                  Confirm tenant type flip
                </div>
              </div>
              <p style={{ ...mono(11, "var(--text, #cfd5e3)"), lineHeight: 1.55 }}>
                <strong>{tenant.name}</strong> is being changed from
                <TenantTypeBadge type={tenant.tenant_type || "forwarder"} />
                to
                <TenantTypeBadge type={pending.to} />.
              </p>
              <div
                className="mt-3 rounded p-3"
                style={{ background: "rgba(243,177,74,0.10)", border: "1px solid rgba(243,177,74,0.30)" }}
                data-testid="tenant-type-flip-confirm-counts"
              >
                <div style={mono(11, "#f3b14a", 600)}>This tenant already has:</div>
                <ul className="mt-1.5 space-y-0.5" style={mono(11, "var(--text-dim, #cfd5e3)")}>
                  <li>· <strong>{pending.impact.deals}</strong> deals</li>
                  <li>· <strong>{pending.impact.relationships}</strong> relationships</li>
                </ul>
                <div className="mt-2" style={mono(10, "var(--text-dim, #9198b0)")}>
                  Existing deals will keep their current tenant_id, but new deals will route under the new tenant_type. Customer-tier visibility will apply server-side immediately.
                </div>
              </div>
            </div>
            <div className="px-4 py-3 flex justify-end gap-2" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <button onClick={() => setPending(null)} className="px-3 py-2 rounded" style={{ ...mono(11, "var(--text-dim, #9aa1b3)"), border: "1px solid rgba(255,255,255,0.15)" }} data-testid="tenant-type-flip-cancel">Cancel</button>
              <button
                onClick={confirmForce}
                className="px-3 py-2 rounded"
                style={{ background: "#E24B4A", color: "#fff", ...mono(11, "#fff", 600) }}
                data-testid="tenant-type-flip-force"
              >
                Yes, flip type anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default function AdminCustomerRelationships() {
  const { data, isLoading, refetch, isFetching } = useCustomerRelationships();
  const { data: tenants = [] } = useAdminAllTenants();
  const del = useDeleteRelationship();
  const [createOpen, setCreateOpen] = useState(false);

  const items = data?.items || [];

  return (
    <div data-testid="admin-customer-relationships-page">
      <header className="mb-5 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: "clamp(22px, 3vw, 28px)", color: "var(--text, #f0f2f8)" }} data-testid="view-title">
            <GitBranch size={20} style={{ display: "inline-block", marginRight: 8, marginBottom: 4 }} />
            Customer Relationships
          </h1>
          <div className="mt-1" style={mono(11, "var(--text-dim, #c5cad8)")}>
            Phase A backbone · Link forwarder ↔ customer / carrier tenants · 3-tier server-side visibility
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="px-3 py-2 rounded inline-flex items-center gap-2"
            style={{ border: "1px solid rgba(255,255,255,0.12)", ...mono(10, "var(--text-dim, #c5cad8)", 600, "0.6px"), textTransform: "uppercase" }}
            data-testid="customer-relationships-refresh"
          >
            <RefreshCw size={12} className={isFetching ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            className="px-3 py-2 rounded inline-flex items-center gap-2"
            style={{ background: "#185FA5", color: "#fff", ...mono(10, "#fff", 700, "0.6px"), textTransform: "uppercase" }}
            data-testid="customer-relationships-new-btn"
          >
            <Plus size={13} /> New link
          </button>
        </div>
      </header>

      {/* Tenants table — every tenant + inline type flip */}
      <section className="mb-6">
        <div style={{ ...mono(11, "var(--text-dim, #c5cad8)", 700, "0.8px"), textTransform: "uppercase", marginBottom: 8 }}>
          All tenants ({tenants.length})
        </div>
        <div
          className="rounded overflow-x-auto"
          style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
          data-testid="tenants-type-table"
        >
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
            <thead>
              <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                {["Name", "Current type", "Set type", "Tenant id"].map((c) => (
                  <th key={c} style={{ padding: "8px 12px", textAlign: "left", ...mono(9, "var(--text-dim, #9aa1b3)", 700, "1.2px"), textTransform: "uppercase", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tenants.length === 0 && (
                <tr><td colSpan={4} style={{ padding: 16, textAlign: "center", ...mono(11, "var(--text-dim, #9aa1b3)") }}>No tenants</td></tr>
              )}
              {tenants.map((t) => (
                <tr key={t.id} data-testid={`tenant-row-${t.id}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                  <td style={{ padding: "8px 12px", ...mono(12, "var(--text, #f0f2f8)") }}>{t.name}</td>
                  <td style={{ padding: "8px 12px" }}><TenantTypeBadge type={t.tenant_type || "forwarder"} /></td>
                  <td style={{ padding: "8px 12px" }}><TenantTypeFlip tenant={t} /></td>
                  <td style={{ padding: "8px 12px", ...mono(10, "var(--text-dimmer, #6b7388)") }}>{t.id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Relationships table */}
      <section>
        <div style={{ ...mono(11, "var(--text-dim, #c5cad8)", 700, "0.8px"), textTransform: "uppercase", marginBottom: 8 }}>
          Relationships ({items.length})
        </div>
        <div
          className="rounded overflow-x-auto"
          style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
          data-testid="customer-relationships-table"
        >
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead>
              <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                {["Forwarder", "Related tenant", "Relation", "Created", ""].map((c) => (
                  <th key={c} style={{ padding: "8px 12px", textAlign: "left", ...mono(9, "var(--text-dim, #9aa1b3)", 700, "1.2px"), textTransform: "uppercase", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={5} style={{ padding: 16, textAlign: "center", ...mono(11, "var(--text-dim, #9aa1b3)") }}><Loader2 size={12} className="animate-spin inline mr-1" />Loading…</td></tr>
              )}
              {!isLoading && items.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 16, textAlign: "center", ...mono(11, "var(--text-dim, #9aa1b3)") }} data-testid="customer-relationships-empty">No relationships yet. Click &quot;New link&quot; to create one.</td></tr>
              )}
              {items.map((r) => (
                <tr key={r.id} data-testid={`relationship-row-${r.id}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                  <td style={{ padding: "8px 12px", ...mono(12, "var(--text, #f0f2f8)") }}>{r.forwarder_name}</td>
                  <td style={{ padding: "8px 12px", ...mono(12, "var(--text, #f0f2f8)") }}>{r.related_name}</td>
                  <td style={{ padding: "8px 12px" }}><TenantTypeBadge type={r.relation_type} /></td>
                  <td style={{ padding: "8px 12px", ...mono(10, "var(--text-dim, #9aa1b3)") }}>{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right" }}>
                    <button
                      onClick={() => {
                        if (window.confirm(`Unlink ${r.related_name}? The tenant itself is NOT deleted.`)) {
                          del.mutate(r.id);
                        }
                      }}
                      className="inline-flex items-center gap-1 rounded px-2 py-1"
                      style={{ background: "rgba(226,75,74,0.10)", color: "#ff8a8a", border: "1px solid rgba(226,75,74,0.30)", ...mono(10, "#ff8a8a", 600, "0.6px") }}
                      data-testid={`relationship-delete-${r.id}`}
                    >
                      <Trash2 size={11} /> Unlink
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <CreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
